import { FileDown, Upload, X } from "lucide-react";
import { type DragEvent, useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import {
  formatDuration,
  type SessionExport,
  type ValidationResult,
  validateSessionLog,
} from "@/lib/session-log.ts";
import { cn } from "@/lib/utils.ts";

interface SessionLogDropZoneProps {
  value: SessionExport | null;
  onChange: (log: SessionExport | null) => void;
}

export function SessionLogDropZone({ value, onChange }: SessionLogDropZoneProps) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(
    (file: File) => {
      setError(null);

      if (!file.name.endsWith(".json")) {
        setError("Only .json files are accepted.");
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setError("File exceeds the 10 MB size limit.");
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = reader.result as string;
          const data: unknown = JSON.parse(text);
          const result: ValidationResult = validateSessionLog(data, file.size);

          if (result.valid) {
            onChange(result.log);
          } else {
            setError(result.error);
          }
        } catch {
          setError("File is not valid JSON.");
        }
      };
      reader.onerror = () => setError("Failed to read file.");
      reader.readAsText(file);
    },
    [onChange],
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
      if (inputRef.current) inputRef.current.value = "";
    },
    [processFile],
  );

  const handleRemove = useCallback(() => {
    onChange(null);
    setError(null);
  }, [onChange]);

  if (value) {
    const s = value.summary;
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
        <div className="mb-3 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <FileDown className="h-4 w-4 text-primary" />
            <span className="font-medium text-sm">Session Log Attached</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleRemove}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-muted-foreground text-xs sm:grid-cols-3">
          <span>
            Session: <span className="text-foreground">{s.session_id}</span>
          </span>
          <span>
            Duration: <span className="text-foreground">{formatDuration(s.duration_ms)}</span>
          </span>
          <span>
            Events: <span className="text-foreground">{s.total_entries}</span>
          </span>
          <span>
            Errors:{" "}
            <span className={cn("text-foreground", s.errors > 0 && "text-destructive")}>
              {s.errors}
            </span>
          </span>
          <span>
            LLM Calls: <span className="text-foreground">{s.llm_calls}</span>
          </span>
          <span>
            Images: <span className="text-foreground">{s.images_generated}</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className={cn(
          "flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors",
          dragActive
            ? "border-primary bg-primary/10 text-foreground"
            : "border-input text-muted-foreground hover:border-primary/50 hover:text-foreground",
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <Upload className="h-6 w-6" />
        <span className="text-sm">
          Drop a <code className="rounded bg-muted px-1 py-0.5 text-xs">reify-session-*.json</code>{" "}
          file here, or click to browse
        </span>
        <span className="text-muted-foreground text-xs">
          Exported from Reify → Settings → Gameplay → Download Session Log
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileChange}
      />
      {error && <p className="mt-2 text-destructive text-sm">{error}</p>}
    </div>
  );
}
